import { type FC, useState, useMemo } from 'react';
import { TournamentCard, type TournamentCardProps } from './TournamentCard';
import { NativeSelect } from '@/components/ui';
import type { Tournament, WeaponType, DivisionType, TournamentStatus } from '@/types';

export interface TournamentListProps {
  tournaments: Tournament[];
  onRegister?: (tournamentId: string) => void;
}

type FilterOption = 'all' | WeaponType | DivisionType | TournamentStatus;

// Label mappings for display
const weaponLabels: Record<string, string> = {
  longsword: 'Longsword',
  saber: 'Saber',
  rapier: 'Rapier',
  'sword-buckler': 'Sword & Buckler',
  cutting: 'Cutting',
  other: 'Other',
};

const divisionLabels: Record<string, string> = {
  open: 'Open',
  womens: "Women's",
  beginner: 'Beginner',
  intermediate: 'Intermediate',
  advanced: 'Advanced',
};

const statusLabels: Record<string, string> = {
  draft: 'Draft',
  open: 'Registration Open',
  closed: 'Registration Closed',
  full: 'Full',
  completed: 'Completed',
};

/**
 * Tournament list component with filtering capabilities
 * Allows users to filter by weapon, division, or status
 */
export const TournamentList: FC<TournamentListProps> = ({ tournaments, onRegister }) => {
  const [weaponFilter, setWeaponFilter] = useState<FilterOption>('all');
  const [divisionFilter, setDivisionFilter] = useState<FilterOption>('all');
  const [statusFilter, setStatusFilter] = useState<FilterOption>('all');

  // Derive filter options from actual data
  const weaponOptions = useMemo(() => {
    const uniqueWeapons = [...new Set(tournaments.map((t) => t.weapon))];
    return [
      { value: 'all', label: 'All Weapons' },
      ...uniqueWeapons.map((w) => ({ value: w, label: weaponLabels[w] || w })),
    ];
  }, [tournaments]);

  const divisionOptions = useMemo(() => {
    const uniqueDivisions = [...new Set(tournaments.map((t) => t.division))];
    return [
      { value: 'all', label: 'All Divisions' },
      ...uniqueDivisions.map((d) => ({ value: d, label: divisionLabels[d] || d })),
    ];
  }, [tournaments]);

  const statusOptions = useMemo(() => {
    const uniqueStatuses = [...new Set(tournaments.map((t) => t.status))];
    return [
      { value: 'all', label: 'All Statuses' },
      ...uniqueStatuses.map((s) => ({ value: s, label: statusLabels[s] || s })),
    ];
  }, [tournaments]);

  // Filter tournaments based on selected filters
  const filteredTournaments = tournaments.filter((tournament) => {
    if (weaponFilter !== 'all' && tournament.weapon !== weaponFilter) return false;
    if (divisionFilter !== 'all' && tournament.division !== divisionFilter) return false;
    if (statusFilter !== 'all' && tournament.status !== statusFilter) return false;
    return true;
  });

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <NativeSelect
            options={weaponOptions}
            value={weaponFilter}
            onChange={(e) => setWeaponFilter(e.target.value as FilterOption)}
            aria-label="Filter by weapon"
          />
        </div>
        <div>
          <NativeSelect
            options={divisionOptions}
            value={divisionFilter}
            onChange={(e) => setDivisionFilter(e.target.value as FilterOption)}
            aria-label="Filter by division"
          />
        </div>
        <div>
          <NativeSelect
            options={statusOptions}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as FilterOption)}
            aria-label="Filter by status"
          />
        </div>
      </div>

      {/* Tournament grid */}
      {filteredTournaments.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTournaments.map((tournament) => {
            const props: TournamentCardProps = {
              tournament,
              ...(onRegister ? { onRegister } : {}),
            };
            return <TournamentCard key={tournament.id} {...props} />;
          })}
        </div>
      ) : (
        <div className="text-center py-12">
          <p className="text-white text-lg">No tournaments found matching your filters.</p>
          <p className="text-orange-200 text-sm mt-2">Try adjusting your filter criteria.</p>
        </div>
      )}
    </div>
  );
};
